import type {ReactElement} from 'react';
import React from 'react';
import {BaseContextualMenuGridPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-contextual-menu-grid-panel/base-contextual-menu-grid-panel';
import type {ContextualMenuGridPanelItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-contextual-menu-grid-panel/base-contextual-menu-grid-panel.types';
import {Suboption} from '@Components/contextual-menu/components/suboption';
import {Text, TextSize} from '@Components/text';
import type {IconInfo} from '@Components/icon-group/icon-group';
import {IconGroup} from '@Components/icon-group/icon-group';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {AnimationSpeed, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {SliderTextOptions} from '@Components/slider-text-options';
import styles from './animation-styles-panel.module.scss';
import {Thumbnail} from '@Components/thumbnail';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ThumbnailItem} from '@Components/thumbnail-item';
import {AnimationTabType, PreviewType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {FeatureDemo} from '@Components/poster-editor/poster-editor.types';
import {updateFeatureDemo} from '@Components/poster-editor/poster-editor-reducer';
import {TabsToggleSize, TabToggleData} from '@Components/tabs-toggle/tabs-toggle.types';
import {TabsToggle} from '@Components/tabs-toggle';

export interface AnimationStylesPanelProps {
  selectedAnimation: AnimationType;
  onAnimationTypeModified: (id: AnimationType) => void;
  onAnimationSpeedModified: (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed) => void;
  onAnimationDirectionModified: (direction: SlideType | PositionType) => void;
  speed: AnimationSpeed;
  direction: SlideType | PositionType;
  animations: AnimationType[];
  showInGrid: boolean;
  previewType?: PreviewType;
  animationTab?: AnimationTabType;
  onAnimationTabChange?: (tab: AnimationTabType) => void;
}

export function AnimationStylesPanel({selectedAnimation = AnimationType.NONE, previewType = PreviewType.DESIGN, ...props}: AnimationStylesPanelProps): ReactElement<any> | null {
  const handleAnimationTypeModified = (animation: AnimationType): void => {
    if (animation !== AnimationType.NONE) {
      const featureDemo = window.PMW.redux.store.getState().posterEditor.featureDemo;
      if (featureDemo === FeatureDemo.ANIMATIONS) {
        trackPosterBuilderGA4Events(GA4EventName.EDITOR_TRY_ANIMATION, {[GA4EventParamName.USER_PREMIUM_LEVEL]: String(getUserPremiumLevel())});
        window.PMW.redux.store.dispatch(updateFeatureDemo(FeatureDemo.NONE));
      }
    }
    props.onAnimationTypeModified(animation);
  };

  const getThumbnailData = (animation: AnimationType): ThumbnailData => {
    if (animation === AnimationType.NONE) {
      return {
        type: ThumbnailType.IMAGE,
        showSkeletonLoading: true,
        imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/none.png`),
      };
    }
    if (animation === selectedAnimation) {
      return {
        type: ThumbnailType.IMAGE,
        showSkeletonLoading: true,
        imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.png?ts=1744006311`),
      };
    }
    if (props.showInGrid) {
      return {
        type: ThumbnailType.VIDEO,
        showSkeletonLoading: true,
        imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.png?ts=1744006311`),
        videoSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.mp4?ts=1745823940`),
      };
    }
    return {
      type: ThumbnailType.IMAGE,
      showSkeletonLoading: true,
      imageSrc: getAssetUrl(`poster-editor-thumbnails/${previewType}-animations/${animation}.png?ts=1744006311`),
    };
  };

  const getThumbnail = (animation: AnimationType): ReactElement<any> => {
    return <Thumbnail key={animation} className={props.showInGrid ? styles.largeImg : styles.smallImg} data={getThumbnailData(animation)} />;
  };

  const getThumbnailItem = (animation: AnimationType): ReactElement<any> => {
    return (
      <ThumbnailItem
        thumbnailContainerClassName={'radius-8'}
        hasBorder={false}
        isSelected={selectedAnimation === animation}
        key={animation}
        thumbnail={getThumbnail(animation)}
        onClick={(): void => {
          if (animation !== selectedAnimation) {
            handleAnimationTypeModified(animation);
          }
        }}
        textClassName={'content-sub-text'}
        text={getNameForAnimationType(animation)}
      />
    );
  };

  const getNameForAnimationType = (animation: AnimationType): string => {
    switch (animation) {
      case AnimationType.BLOCK:
        return window.i18next.t('pmwjs_block');
      case AnimationType.BOUNCE:
        return window.i18next.t('pmwjs_bounce');
      case AnimationType.FADE:
        return window.i18next.t('pmwjs_fade');
      case AnimationType.JELLO:
        return window.i18next.t('pmwjs_jello');
      case AnimationType.PAN:
        return window.i18next.t('pmwjs_pan');
      case AnimationType.POP:
        return window.i18next.t('pmwjs_pop');
      case AnimationType.ROTATE:
        return window.i18next.t('pmwjs_rotate');
      case AnimationType.SCALE:
        return window.i18next.t('pmwjs_scale');
      case AnimationType.SLIDE:
        return window.i18next.t('pmwjs_slide');
      case AnimationType.TUMBLE:
        return window.i18next.t('pmwjs_tumble');
      case AnimationType.WIPE:
        return window.i18next.t('pmwjs_wipe');
      case AnimationType.PEEK:
        return window.i18next.t('pmwjs_peek');
      case AnimationType.ZOOM:
        return window.i18next.t('pmwjs_zoom');
      case AnimationType.NONE:
      default:
        return window.i18next.t('pmwjs_none');
    }
  };
  const getItems = (): ContextualMenuGridPanelItem[] => {
    return props.animations.map((animation) => {
      return {
        id: animation,
        name: getNameForAnimationType(animation),
        thumb: getThumbnail(animation),
      };
    });
  };

  const getTextForSpeed = (speed: AnimationSpeed): string => {
    switch (speed) {
      case AnimationSpeed.HIGHER:
        return window.i18next.t('pmwjs_higher');
      case AnimationSpeed.HIGH:
        return window.i18next.t('pmwjs_high');
      case AnimationSpeed.LOWER:
        return window.i18next.t('pmwjs_lower');
      case AnimationSpeed.LOW:
        return window.i18next.t('pmwjs_low');
      case AnimationSpeed.MEDIUM:
      default:
        return window.i18next.t('pmwjs_medium');
    }
  };

  const getSliderOption = (): ReactElement<any> => {
    return (
      <SliderTextOptions
        data={[AnimationSpeed.LOWER, AnimationSpeed.LOW, AnimationSpeed.MEDIUM, AnimationSpeed.HIGH, AnimationSpeed.HIGHER]}
        label={window.i18next.t('pmwjs_speed')}
        className={props.showInGrid ? '' : 'spacing-p-b-3'}
        value={getTextForSpeed(props.speed)}
        selectedId={props.speed}
        onChange={(speed: string): void => {
          if (speed !== props.speed.toString()) {
            props.onAnimationSpeedModified(false, false, speed as AnimationSpeed);
          }
        }}
        onChangeEnded={(speed: string): void => {
          if (speed !== props.speed.toString()) {
            props.onAnimationSpeedModified(true, true, speed as AnimationSpeed);
          }
        }}
      />
    );
  };

  const getIcons = (): IconInfo[] => {
    switch (selectedAnimation) {
      case AnimationType.ROTATE:
        return [
          {
            id: PositionType.LEFT,
            name: 'icon-rotate-cw',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.RIGHT,
            name: 'icon-rotate-ccw',
            isSelected: props.direction === PositionType.RIGHT,
          },
        ];
      case AnimationType.SCALE:
      case AnimationType.ZOOM:
        return [
          {
            id: PositionType.LEFT,
            name: 'icon-arrows-maximize',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.RIGHT,
            name: 'icon-arrows-minimize',
            isSelected: props.direction === PositionType.RIGHT,
          },
        ];
      default:
        return [
          {
            id: PositionType.RIGHT,
            name: 'icon-arrow-right',
            isSelected: props.direction === PositionType.RIGHT,
          },
          {
            id: PositionType.LEFT,
            name: 'icon-arrow-left',
            isSelected: props.direction === PositionType.LEFT,
          },
          {
            id: PositionType.TOP,
            name: 'icon-arrow-top',
            isSelected: props.direction === PositionType.TOP,
          },
          {
            id: PositionType.BOTTOM,
            name: 'icon-arrow-bottom',
            isSelected: props.direction === PositionType.BOTTOM,
          },
        ];
    }
  };

  const getDirectionOptions = (): ReactElement<any> | undefined => {
    if (!shouldShowDirection()) {
      return;
    }
    return (
      <div className={`flex-row-justify-between flex-row-align-center ${props.showInGrid ? '' : 'spacing-p-b-3'}`}>
        <Text val={window.i18next.t('pmwjs_direction')} size={TextSize.XSMALL} />
        <IconGroup
          size={IconSize.SIZE_ICON_16}
          onIconSelect={(id: string): void => {
            if (id !== props.direction.toString()) {
              props.onAnimationDirectionModified(id as PositionType);
            }
          }}
          icons={getIcons()}
        />
      </div>
    );
  };

  const shouldShowSubOptions = (): boolean => {
    return ![AnimationType.NONE].includes(selectedAnimation);
  };

  const shouldShowDirection = (): boolean => {
    return ![AnimationType.JELLO, AnimationType.POP, AnimationType.FADE, AnimationType.TUMBLE].includes(selectedAnimation);
  };

  const getAnimationTabsData = (): TabToggleData[] => {
    return [
      {id: AnimationTabType.ENTRANCE, text: window.i18next.t('pmwjs_entrance')},
      {id: AnimationTabType.EXIT, text: window.i18next.t('pmwjs_exit')},
      {id: AnimationTabType.BOTH, text: window.i18next.t('pmwjs_both')},
    ];
  };

  const getAnimationTabs = (): ReactElement<any> | null => {
    if (!props.animationTab || !props.onAnimationTabChange) {
      return null;
    }
    return (
      <div className="spacing-p-l-5 spacing-p-r-5 spacing-m-b-4 gap-2 _full-width">
        <TabsToggle
          size={TabsToggleSize.XSMALL}
          className="_full-width"
          data={getAnimationTabsData()}
          selectedId={props.animationTab}
          onClick={(id: string): void => {
            props.onAnimationTabChange?.(id as AnimationTabType);
          }}
        />
        {props.animationTab === AnimationTabType.BOTH ? (
          <Text val={window.i18next.t('pmwjs_applies_both_in_and_out_animations')} size={TextSize.XXSMALL} className={'content-text spacing-m-b-3'} />
        ) : null}
      </div>
    );
  };

  const getSubOptionForGrid = (): ReactElement<any> | undefined => {
    if (!shouldShowSubOptions()) {
      return;
    }
    return (
      <Suboption
        className={'spacing-m-b-5'}
        children={
          <>
            {getSliderOption()}
            {getDirectionOptions()}
          </>
        }
      />
    );
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (!shouldShowSubOptions()) {
      return null;
    }
    return (
      <>
        {getSliderOption()}
        {getDirectionOptions()}
      </>
    );
  };

  if (props.showInGrid) {
    return (
      <div className={'flex-column-justify-center flex-items-center'}>
        {getAnimationTabs()}
        <BaseContextualMenuGridPanel
          hasBorder={false}
          items={getItems()}
          hasCustomDimensions={true}
          selectedId={selectedAnimation}
          containerClass={styles.gridContainer}
          hasSubOptions={true}
          subOptionToShow={getSubOptionForGrid()}
          showNameWithThumb={true}
          onSelected={(id: string) => {
            if (id !== selectedAnimation.toString()) {
              handleAnimationTypeModified(id as AnimationType);
            }
          }}
        />
      </div>
    );
  }

  return (
    <ContextualMenu id="animation-styles-mobile-panel" hasMobileModalPadding hasMobileMorphingProperties>
      {getAnimationTabs()}
      {getSubOptions()}
      <div>
        <div className={`flexbox ${styles.items}`}>
          {props.animations.map((animation) => {
            return getThumbnailItem(animation);
          })}
        </div>
      </div>
    </ContextualMenu>
  );
}
